import React from 'react';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { CardText } from 'material-ui/Card';
import RequestItem from './RequestItem.jsx';
import Loader from './../../../../components/Loader';
import CircularLoading from '../../../../components/CircularLoading';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { useLanguage } from '../../../../context/LanguageContext';


// Row wrapper only for sortable subset
const SortableRow = SortableElement(({ preference, displayName, propsRef, visualIndex, isCombined }) => (
  <RequestItem
    preference={preference}
    index={visualIndex}
    displayName={displayName}
    groupName={preference.groupName}
    isCombined={isCombined}
    bidSession={propsRef.bidSession}
    handleDeletePreference={propsRef.handleDeletePreference}
    handleDeleteCombinedPreference={propsRef.handleDeleteCombinedPreference}
    handleMovePreference={propsRef.handleMovePreference}
    handleWeightEdit={propsRef.handleWeightEdit}
    handleNext={propsRef.handleNext}
    readOnly={propsRef.readOnly}
    copyPreference={propsRef.copyPreference}
    totalEntries={propsRef.preferences?.length || 0}
  />
));

// Container for sortable subset only
const SortableSubset = SortableContainer(({ items, startVisualIndex, propsRef, labels }) => {
  const displayedGroupNames = new Set();
  return (
    <div>
      {items.map((preference, subsetIdx) => {
        const visualIndex = startVisualIndex + subsetIdx;
        const isCombined = preference.groupName && preference.groupName.trim() !== '';
        const showGroup = isCombined && !displayedGroupNames.has(preference.groupName);
        if (showGroup) displayedGroupNames.add(preference.groupName);
        if (!isCombined || showGroup) {
          return (
            <SortableRow
              key={preference.bidAggregateId}
              index={subsetIdx}
              preference={preference}
              propsRef={propsRef}
              visualIndex={visualIndex}
              isCombined={isCombined}
              disabled={propsRef.readOnly}
              displayName={showGroup && isCombined ? `${preference.groupName} - ${labels.REPORT?.COMBINED || 'Combined'}` : ''}
            />
          );
        }
        return null;
      })}
    </div>
  );
});

const RequestsList = (props) => {
  const { labels } = useLanguage();
  const commonLabels = labels.commonLabels || {};
  const HISTORY = labels.HISTORY || {};
  const REPORT = labels.REPORT || {};
  const WEIGHT = labels.WEIGHT || 'Weight';

  const [isExpanded, setIsExpanded] = React.useState(false);
  const [contentHeight, setContentHeight] = React.useState(0);
  const containerRef = React.useRef(null);
  const contentRef = React.useRef(null);
  const scrollIntervalRef = React.useRef(null);
  const handleSortMoveRef = React.useRef(null);
  const autoscrollSpeedRef = React.useRef(0);
  const initialScrollTopRef = React.useRef(0);
  const itemHeightRef = React.useRef(30);
  const lastMouseYRef = React.useRef(0);

  const {
    preferences = [],
    loadingBidTable,
    bidSession,
    readOnly,
    requestAll,
    onSortStart,
    onSortEnd,
    helperClass,
    distance = 1,
    pressDelay = 0,
    useDragHandle = true
  } = props;

  // Auto-scroll when dragging near edges
  const handleAutoScroll = React.useCallback((event) => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const scrollThreshold = 100;
    const maxScrollSpeed = 20;

    if (!event) return;

    const mouseY = event.clientY || (event.touches && event.touches[0] ? event.touches[0].clientY : null);
    if (!mouseY) return;

    const distanceFromTop = mouseY - rect.top;
    const distanceFromBottom = rect.bottom - mouseY;

    let scrollSpeed = 0;

    // Allow scrolling even when mouse is ABOVE the container
    if (distanceFromTop < scrollThreshold && container.scrollTop > 0) {
      let speedFactor;
      if (distanceFromTop < 0) {
        // Mouse is ABOVE container - use super fast scrolling
        speedFactor = Math.min(2.0, 1.0 + Math.abs(distanceFromTop) / 100);
      } else {
        // Mouse is within threshold inside container
        speedFactor = 1 - (distanceFromTop / scrollThreshold);
      }
      scrollSpeed = -Math.max(5, maxScrollSpeed * speedFactor);
    } else if (distanceFromBottom >= 0 && distanceFromBottom < scrollThreshold &&
      container.scrollTop < container.scrollHeight - container.clientHeight) {
      // Scroll down
      const speedFactor = 1 - (distanceFromBottom / scrollThreshold);
      scrollSpeed = Math.max(5, maxScrollSpeed * speedFactor);
    }

    autoscrollSpeedRef.current = scrollSpeed;

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    if (scrollSpeed !== 0) {
      scrollIntervalRef.current = setInterval(() => {
        const currentContainer = containerRef.current;
        if (currentContainer) {
          const speed = autoscrollSpeedRef.current;
          if (speed < 0 && currentContainer.scrollTop > 0) {
            currentContainer.scrollTop = Math.max(0, currentContainer.scrollTop + speed);
          } else if (speed > 0 && currentContainer.scrollTop < currentContainer.scrollHeight - currentContainer.clientHeight) {
            currentContainer.scrollTop = Math.min(
              currentContainer.scrollHeight - currentContainer.clientHeight,
              currentContainer.scrollTop + speed
            );
          }
        }
      }, 16);
    }
  }, []);

  const handleSortMove = React.useCallback((event) => {
    // Handle both mouse and touch events
    const processedEvent = event.touches ? { clientY: event.touches[0].clientY } : event;

    // Track the last mouse Y position
    if (processedEvent.clientY) {
      lastMouseYRef.current = processedEvent.clientY;
    }

    handleAutoScroll(processedEvent);
  }, [handleAutoScroll]);

  // Store ref for cleanup
  React.useEffect(() => {
    handleSortMoveRef.current = handleSortMove;
  }, [handleSortMove]);

  const handleSortStartWithScroll = React.useCallback((sortProps) => {
    // Store initial scroll position
    if (containerRef.current) {
      initialScrollTopRef.current = containerRef.current.scrollTop;

      // Calculate actual item height from the first sortable item
      const firstItem = containerRef.current.querySelector('.bid-body');
      if (firstItem) {
        itemHeightRef.current = firstItem.offsetHeight;
      }

    }

    if (onSortStart) {
      onSortStart(sortProps);
    }
    if (handleSortMoveRef.current) {
      document.addEventListener('mousemove', handleSortMoveRef.current);
      document.addEventListener('touchmove', handleSortMoveRef.current);
    }
  }, [onSortStart]);

  const handleSortEndWithScroll = React.useCallback((sortProps) => {
    const currentScrollTop = containerRef.current ? containerRef.current.scrollTop : 0;
    const scrollDelta = initialScrollTopRef.current - currentScrollTop;

    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    if (handleSortMoveRef.current) {
      document.removeEventListener('mousemove', handleSortMoveRef.current);
      document.removeEventListener('touchmove', handleSortMoveRef.current);
    }

    if (onSortEnd) {
      // Calculate sortable bids count locally
      const sortableCount = preferences.filter(p => p.scoreApplicable).length;
      const nonSortableCount = preferences.length - sortableCount;

      const offset = nonSortableCount;
      let adjustedNewIndex = sortProps.newIndex;

      // Calculate the actual drop position based on mouse Y position and current scroll
      if (containerRef.current && lastMouseYRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        let mouseYRelativeToContainer = lastMouseYRef.current - containerRect.top;

        // Clamp mouse position to container bounds (this matches the visual helper behavior)
        if (mouseYRelativeToContainer < 0) {
          mouseYRelativeToContainer = 0; // Mouse is above container, treat as top
        } else if (mouseYRelativeToContainer > containerRect.height) {
          mouseYRelativeToContainer = containerRect.height; // Mouse is below container, treat as bottom
        }

        // Calculate which item index the mouse is over in the visible area
        const itemHeight = itemHeightRef.current;
        const scrollTop = containerRef.current.scrollTop;

        // Calculate offset to sortable items using offsetTop (not screen position)
        const sortableContainer = containerRef.current.querySelector('.sortable-container');
        let offsetBeforeSortable = 0;
        if (sortableContainer) {
          // Use offsetTop which is relative to the container, not the screen
          offsetBeforeSortable = sortableContainer.offsetTop;
        }

        // Mouse position relative to container top + scroll position = absolute position in content
        const absoluteYInContent = scrollTop + mouseYRelativeToContainer;

        // Subtract the offset to get position relative to sortable items start
        const mouseYRelativeToItems = Math.max(0, absoluteYInContent - offsetBeforeSortable);

        // Helper to clamp index to valid range
        const clampIndex = (idx) => Math.max(0, Math.min(sortableCount - 1, idx));

        // Calculate the index based on position within sortable items
        const calculatedIndex = Math.floor(mouseYRelativeToItems / itemHeight);
        const calculatedWithScroll = clampIndex(calculatedIndex);

        // Alternative: Adjust library index by scroll offset
        const scrolledItems = Math.round(scrollDelta / itemHeight);
        const libraryAdjusted = clampIndex(sortProps.newIndex - scrolledItems);

        // Adaptive approach: Use mouse position for large scrolls (>200px), library-adjusted for small scrolls
        // Large scrolls: library's cached positions are too stale, trust mouse
        // Small scrolls: library-adjusted preserves visual intent better
        adjustedNewIndex = Math.abs(scrollDelta) > 200 ? calculatedWithScroll : libraryAdjusted;

      }

      const finalOldIndex = sortProps.oldIndex + offset;
      const finalNewIndex = adjustedNewIndex + offset;
      onSortEnd({ oldIndex: finalOldIndex, newIndex: finalNewIndex });
    }
  }, [onSortEnd, preferences]);

  React.useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
      if (handleSortMoveRef.current) {
        document.removeEventListener('mousemove', handleSortMoveRef.current);
        document.removeEventListener('touchmove', handleSortMoveRef.current);
      }
    };
  }, []);

  // Measure content height to determine if expand/collapse should be shown
  React.useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [preferences]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Calculate the container height based on content and expand state
  const getContainerHeight = () => {
    const minHeight = 300;
    const maxHeight = 600;

    if (contentHeight === 0) {
      return minHeight; // Default before content is measured
    }

    if (isExpanded) {
      // When expanded, use content height between min and max
      return Math.max(minHeight, Math.min(contentHeight, maxHeight));
    } else {
      // When collapsed, use min height (300px) or content height if larger but under min
      return Math.max(minHeight, Math.min(contentHeight, minHeight));
    }
  };

  const containerHeight = getContainerHeight();

  if (loadingBidTable) {
    return <CardText className={'rows-container'}><CircularLoading big={false} /></CardText>;
  }
  if (!preferences || !preferences.length) {
    return <CardText className={'rows-container'}><p className="no-bids-message">{HISTORY.NO_BIDS_MESSAGE || 'No bids available'}</p></CardText>;
  }

  const nonSortableBids = [];
  const sortableBids = [];
  for (const p of preferences) {
    if (!p.scoreApplicable) {
      nonSortableBids.push(p);
    } else {
      sortableBids.push(p);
    }
  }

  return (
    <div className="requests-wrapper">
      <div
        ref={containerRef}
        className="rows-container requests-container"
        data-scrollable="true"
        style={{
          height: `${containerHeight}px`,
          maxHeight: `${containerHeight}px`,
          overflowY: contentHeight > containerHeight ? 'auto' : 'hidden'
        }}
      >
        <div ref={contentRef}>
          {bidSession && bidSession.bidMode !== commonLabels.MANUAL ? (
            <div className="row expanded bid-title">
              <div>{props.title}</div>
            </div>
          ) : (
            <div className="row expanded">
              <span className="weighted" onClick={props.onSorting}>
                {WEIGHT}
                <div className={props.isAscending ? 'ion ion-arrow-up-b' : 'ion ion-arrow-down-b'} />
              </span>
              <span className="requests">{props.title}</span>
            </div>
          )}

          {requestAll ? <Loader className={'requestAll-loader'} /> : (
            <>
              {nonSortableBids.map((preference, idx) => {
                const isCombined = preference.groupName && preference.groupName.trim() !== '';
                return (
                  <RequestItem
                    key={preference.bidAggregateId}
                    preference={preference}
                    index={idx}
                    displayName={isCombined ? `${preference.groupName} - ${REPORT.COMBINED || 'Combined'}` : ''}
                    groupName={preference.groupName}
                    isCombined={isCombined}
                    bidSession={bidSession}
                    handleDeletePreference={props.handleDeletePreference}
                    handleDeleteCombinedPreference={props.handleDeleteCombinedPreference}
                    handleMovePreference={props.handleMovePreference}
                    handleWeightEdit={props.handleWeightEdit}
                    handleNext={props.handleNext}
                    readOnly={readOnly}
                    copyPreference={props.copyPreference}
                    totalEntries={preferences.length}
                  />
                );
              })}

              {nonSortableBids.length > 0 && sortableBids.length > 0 && <hr className='bid-separator' />}

              <div className={`sortable-container ${props.isSorting ? 'isSorting' : ''}`}>
                <SortableSubset
                  items={sortableBids}
                  startVisualIndex={nonSortableBids.length}
                  propsRef={props}
                  labels={labels}
                  axis='y'
                  lockAxis='y'
                  helperClass={helperClass}
                  distance={distance}
                  pressDelay={pressDelay}
                  useDragHandle={useDragHandle}
                  onSortStart={handleSortStartWithScroll}
                  onSortEnd={handleSortEndWithScroll}
                  onSortMove={handleSortMove}
                  lockToContainerEdges={true}
                  useWindowAsScrollContainer={false}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <button
        className="expand-collapse-button"
        onClick={toggleExpand}
        type="button"
      >
        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </button>
    </div>
  );
};

export default RequestsList;